export const AUTH_TOKEN_KEY = 'ontrack-auth-token'
export const AUTH_STATE_EVENT = 'ontrack-auth-state-change'

const NAME_INITIALS_KEY = 'ontrack-name-initials'
const CURRENT_USER_KEY = 'ontrack-current-user'
const CURRENT_USER_NAME_KEY = 'ontrack-name'
const USERDATA_KEY = 'ontrack-userdata'
const ORGANIZATION_KEY = 'ontrack-organization'
const TOKEN_EXPIRY_SKEW_SECONDS = 30

const readJson = (key) => {
  try {
    const value = localStorage.getItem(key)
    return value ? JSON.parse(value) : null
  } catch (error) {
    return null
  }
}

const writeJson = (key, value) => {
  if (!value) {
    localStorage.removeItem(key)
    return
  }

  localStorage.setItem(key, JSON.stringify(value))
}

export const getAuthToken = () =>
  {
    const token =
      sessionStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY)

    if (!token) {
      return null
    }

    if (!isTokenUsable(token)) {
      localStorage.removeItem(AUTH_TOKEN_KEY)
      sessionStorage.removeItem(AUTH_TOKEN_KEY)
      return null
    }

    return token
  }

export const hasAuthToken = () => Boolean(getAuthToken())

export const getStoredUserData = () => readJson(USERDATA_KEY)

export const getStoredOrganizationData = () => readJson(ORGANIZATION_KEY)

const getInitials = (fullName) => {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean)
  const first = parts[0] ? parts[0][0] : ''
  const last = parts.length > 1 ? parts[parts.length - 1][0] : parts[0]?.[1] || ''
  return (first + last).toUpperCase()
}

const decodeBase64Url = (value) => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const padding = normalized.length % 4
  const padded =
    padding === 0 ? normalized : normalized + '='.repeat(4 - padding)

  try {
    return JSON.parse(atob(padded))
  } catch (error) {
    return null
  }
}

export const getTokenPayload = (token) => {
  if (!token) {
    return null
  }

  const parts = token.split('.')
  if (parts.length !== 3) {
    return null
  }

  return decodeBase64Url(parts[1])
}

export const getTokenExpiration = (token) => {
  const payload = getTokenPayload(token)
  const exp = payload?.exp

  if (typeof exp !== 'number') {
    return null
  }

  return new Date(exp * 1000)
}

export const isTokenExpired = (token) => {
  const payload = getTokenPayload(token)
  const exp = payload?.exp

  if (typeof exp !== 'number') {
    return true
  }

  const nowInSeconds = Math.floor(Date.now() / 1000)
  return exp <= nowInSeconds + TOKEN_EXPIRY_SKEW_SECONDS
}

export const isTokenUsable = (token) => {
  const payload = getTokenPayload(token)
  if (!payload) {
    return false
  }

  return !isTokenExpired(token)
}

export const getDebugSnapshot = () => {
  const token =
    sessionStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY)
  const userData = getStoredUserData()
  const organizationData = getStoredOrganizationData()
  const currentUserName =
    sessionStorage.getItem(CURRENT_USER_NAME_KEY) || userData?.fullName || null
  const currentUserEmail =
    localStorage.getItem(CURRENT_USER_KEY) || userData?.email || null
  const currentUserInitials =
    localStorage.getItem(NAME_INITIALS_KEY) || getInitials(currentUserName) || null

  return {
    isLoggedIn: hasAuthToken(),
    authTokenPresent: Boolean(token),
    authTokenUsable: Boolean(token) && isTokenUsable(token),
    authTokenExpiresAt: getTokenExpiration(token)?.toISOString() || null,
    currentUserName,
    currentUserEmail,
    currentUserInitials,
    userData,
    organizationData,
  }
}

export const syncOntrackDebug = () => {
  if (typeof window === 'undefined') {
    return
  }

  window.__ONTRACK_DEBUG__ = getDebugSnapshot()
}

const notifyAuthStateChange = () => {
  if (typeof window === 'undefined') {
    return
  }

  const snapshot = getDebugSnapshot()
  window.__ONTRACK_DEBUG__ = snapshot
  window.dispatchEvent(
    new CustomEvent(AUTH_STATE_EVENT, {
      detail: snapshot,
    })
  )
}

export const setAuthToken = (token, storage = 'local') => {
  const targetStorage = storage === 'session' ? sessionStorage : localStorage
  const otherStorage = storage === 'session' ? localStorage : sessionStorage

  if (!token) {
    clearAuthStorage()
    return
  }

  targetStorage.setItem(AUTH_TOKEN_KEY, token)
  otherStorage.removeItem(AUTH_TOKEN_KEY)
  notifyAuthStateChange()
}

export const storeUserSession = ({ userData, organizationData } = {}) => {
  const fullName = userData?.fullName || ''
  const email = userData?.email || ''
  const initials = getInitials(fullName)

  if (email) {
    localStorage.setItem(CURRENT_USER_KEY, email)
  } else {
    localStorage.removeItem(CURRENT_USER_KEY)
  }

  if (fullName) {
    sessionStorage.setItem(CURRENT_USER_NAME_KEY, fullName)
  } else {
    sessionStorage.removeItem(CURRENT_USER_NAME_KEY)
  }

  if (initials) {
    localStorage.setItem(NAME_INITIALS_KEY, initials)
  } else {
    localStorage.removeItem(NAME_INITIALS_KEY)
  }

  writeJson(USERDATA_KEY, userData || null)
  writeJson(ORGANIZATION_KEY, organizationData || null)
  notifyAuthStateChange()
}

export const clearAuthStorage = ({ notify = true } = {}) => {
  localStorage.removeItem(AUTH_TOKEN_KEY)
  sessionStorage.removeItem(AUTH_TOKEN_KEY)
  localStorage.removeItem(NAME_INITIALS_KEY)
  localStorage.removeItem(CURRENT_USER_KEY)
  sessionStorage.removeItem(CURRENT_USER_NAME_KEY)
  localStorage.removeItem(USERDATA_KEY)
  localStorage.removeItem(ORGANIZATION_KEY)
  if (notify) {
    notifyAuthStateChange()
  }
}

export const isPublicPath = (pathname) => {
  const publicPaths = new Set([
    '/',
    '/signin',
    '/signup-org-user',
    '/signuporguser',
    '/verify-main-user-email',
    '/verifymainuseremail',
    '/reset-password',
    '/resetpassword',
    '/actual-reset-password',
    '/actualresetpassword',
    '/invite',
  ])

  return publicPaths.has((pathname || '').toLowerCase())
}

export const redirectToSignIn = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (isPublicPath(window.location.pathname)) {
    return
  }

  window.history.replaceState(window.history.state, '', '/signin')
  window.dispatchEvent(new PopStateEvent('popstate'))
}

export const handleUnauthorized = () => {
  clearAuthStorage()
  redirectToSignIn()
}

export const subscribeToAuthChanges = (listener) => {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const handleChange = () => listener()
  const handleStorage = (event) => {
    if (
      [
        AUTH_TOKEN_KEY,
        NAME_INITIALS_KEY,
        CURRENT_USER_KEY,
        CURRENT_USER_NAME_KEY,
        USERDATA_KEY,
        ORGANIZATION_KEY,
      ].includes(event.key)
    ) {
      listener()
    }
  }

  window.addEventListener(AUTH_STATE_EVENT, handleChange)
  window.addEventListener('storage', handleStorage)

  return () => {
    window.removeEventListener(AUTH_STATE_EVENT, handleChange)
    window.removeEventListener('storage', handleStorage)
  }
}

if (typeof window !== 'undefined') {
  syncOntrackDebug()
}
